import type { RequestConfig } from '@@/plugin-request/types';
import { LinkOutlined } from '@ant-design/icons';
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import { history, Link } from '@umijs/max';
import React from 'react';
import { AvatarDropdown, AvatarName, Footer } from '@/components';
import ActivityProcessAccessFallback from '@/components/ActivityProcessAccessFallback';
import ActivityProcessEntrySync from '@/components/ActivityProcessEntrySync';
import AuthSessionRecoverySync from '@/components/AuthSessionRecoverySync';
import { loadActivityProcessEntry } from '@/utils/activityProcessEntry';
import { isAssetSharePath } from '@/utils/assetLibrary/publicShareService';
import { getSessionCurrentUser } from '@/utils/authApi';
import {
  type CurrentUser,
  getAuthSessionGeneration,
  hasAuthSession,
} from '@/utils/authSession';
import { isLotteryScreenPath } from '@/utils/lotteryScreenRequest';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';

const isDev = process.env.NODE_ENV === 'development';
const loginPath = '/user/login';
const forceChangePasswordPath = '/user/force-change-password';

interface InitialState {
  authRecoveryRevision?: string;
  activityProcessEntry?: { ownerId: string; visible: boolean };
  settings?: Partial<LayoutSettings>;
  currentUser?: CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<CurrentUser | undefined>;
}

interface LayoutRuntimeProps {
  initialState?: InitialState;
  setInitialState: (
    state: InitialState | ((state: InitialState) => InitialState),
  ) => void;
}

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<InitialState> {
  const fetchUserInfo = async () => {
    return getSessionCurrentUser();
  };
  // 如果不是登录页面，执行
  const location =
    typeof window === 'undefined' ? { pathname: loginPath } : window.location;
  if (
    !isAssetSharePath(location.pathname) &&
    !isLotteryScreenPath(location.pathname) &&
    ![loginPath, '/user/register', '/user/register-result'].includes(
      location.pathname,
    )
  ) {
    const currentUser = await fetchUserInfo();
    const generation = getAuthSessionGeneration();
    const ownerId = currentUser?.id;
    const entry = ownerId
      ? await loadActivityProcessEntry().catch(() => undefined)
      : undefined;
    const sameSession = generation === getAuthSessionGeneration();
    return {
      activityProcessEntry:
        ownerId && sameSession && entry
          ? { ownerId, visible: entry.visible === true }
          : undefined,
      fetchUserInfo,
      currentUser: sameSession ? currentUser : undefined,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }
  return {
    fetchUserInfo,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout = ({
  initialState,
  setInitialState,
}: LayoutRuntimeProps) => {
  return {
    actionsRender: () => [],
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      title: <AvatarName />,
      render: (_: React.ReactNode, avatarChildren: React.ReactNode) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    waterMarkProps: {
      content: initialState?.currentUser?.name,
    },
    footerRender: () => <Footer />,
    onPageChange: () => {
      const { location } = window;
      if (
        isAssetSharePath(location.pathname) ||
        isLotteryScreenPath(location.pathname)
      ) return;
      // 如果没有登录，重定向到 login
      if (
        !initialState?.currentUser &&
        !hasAuthSession() &&
        location.pathname !== loginPath
      ) {
        history.push(loginPath);
        return;
      }
      if (
        initialState?.currentUser?.isFirstLogin &&
        location.pathname !== forceChangePasswordPath
      ) {
        history.replace(
          `${forceChangePasswordPath}?${new URLSearchParams({
            redirect: location.pathname + location.search,
          }).toString()}`,
        );
      }
    },
    bgLayoutImgList: [],
    links: isDev
      ? [
          <Link key="openapi" to="/umi/plugin/openapi" target="_blank">
            <LinkOutlined />
            <span>OpenAPI 文档</span>
          </Link>,
      ]
      : [],
    menuHeaderRender: undefined,
    unAccessible: <ActivityProcessAccessFallback />,
    // 增加一个 loading 的状态
    childrenRender: (children: React.ReactNode) => {
      // if (initialState?.loading) return <PageLoading />;
      return (
        <>
          <AuthSessionRecoverySync />
          <ActivityProcessEntrySync />
          {children}
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((previousState) => ({
                  ...previousState,
                  settings,
                }));
              }}
            />
          )}
        </>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
  ...errorConfig,
};
